import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  Box,
  CircularProgress,
} from '@mui/material';
import PaginationBar from '../../common/PaginationBar';
import {
  ExperimentTableHeader,
  ExperimentTableRow,
  ExperimentActionsMenu,
  EmptyState,
  useExperimentList,
} from './ExperimentListComponents';

const ExperimentList = ({
  experiments,
  loading,
  onExperimentClick,
  onEditClick,
  onStatusChange,
  onStatusFilterChange,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  statusFilter,
}) => {
  const {
    templateNames,
    statusFilterAnchor,
    actionMenuAnchor,
    selectedExperiment,
    fetchTemplateNames,
    handleStatusFilterOpen,
    handleStatusFilterClose,
    handleActionMenuOpen,
    handleActionMenuClose,
  } = useExperimentList();

  // Fetch template names when experiments change
  React.useEffect(() => {
    fetchTemplateNames(experiments);
  }, [experiments, fetchTemplateNames]);

  const handleSort = column => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      onSortChange(column, newOrder);
    } else {
      // Set new column and default to descending for date columns
      const defaultOrder =
        column === 'created_at' || column === 'updated_at' ? 'desc' : 'asc';
      onSortChange(column, defaultOrder);
    }
  };

  const handleStatusFilterSelect = status => {
    onStatusFilterChange(status);
    handleStatusFilterClose();
  };

  const handleMenuAction = action => {
    if (!selectedExperiment) return;

    handleActionMenuClose();

    switch (action) {
      case 'view':
        onExperimentClick(selectedExperiment);
        break;
      case 'edit':
        onEditClick(selectedExperiment);
        break;
      case 'start':
        onStatusChange(selectedExperiment.id, 'start');
        break;
      case 'pause':
        onStatusChange(selectedExperiment.id, 'pause');
        break;
      case 'resume':
        onStatusChange(selectedExperiment.id, 'start');
        break;
      case 'stop':
        onStatusChange(selectedExperiment.id, 'stop');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!experiments || experiments.length === 0) {
    return <EmptyState />;
  }

  return (
    <Paper
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <TableContainer>
        <Table>
          <ExperimentTableHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            statusFilter={statusFilter}
            onSort={handleSort}
            onStatusFilterOpen={handleStatusFilterOpen}
            onStatusFilterClose={handleStatusFilterClose}
            onStatusFilterSelect={handleStatusFilterSelect}
            statusFilterAnchor={statusFilterAnchor}
          />
          <TableBody>
            {experiments.map(experiment => (
              <ExperimentTableRow
                key={experiment.id}
                experiment={experiment}
                templateNames={templateNames}
                onExperimentClick={onExperimentClick}
                onActionMenuOpen={handleActionMenuOpen}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 2 }}>
        <PaginationBar
          currentPage={page + 1} // Convert 0-based to 1-based
          totalPages={Math.ceil(totalCount / rowsPerPage) || 1}
          totalCount={totalCount}
          pageSize={rowsPerPage}
          onPrev={() => onPageChange(null, page - 1)}
          onNext={() => onPageChange(null, page + 1)}
          onPageClick={newPage => onPageChange(null, newPage - 1)} // Convert 1-based to 0-based
          onPageSizeChange={newSize =>
            onRowsPerPageChange({ target: { value: newSize } })
          }
        />
      </Box>

      <ExperimentActionsMenu
        anchorEl={actionMenuAnchor}
        selectedExperiment={selectedExperiment}
        experiments={experiments}
        onClose={handleActionMenuClose}
        onMenuAction={handleMenuAction}
      />
    </Paper>
  );
};

export default ExperimentList;
